import Inputmask from 'inputmask';
import PropTypes from 'prop-types';
import { useRef, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import TextField from '@mui/material/TextField';

// ----------------------------------------------------------------------
// Campo de número de documento de identidad. Existe para que la regla de
// formato viva en UN solo sitio: hasta ahora cada pantalla montaba su propio
// `useEffect` + `useRef` + `new Inputmask(...)` sobre un RHFTextField
// ajeno (admission-new-view.jsx, patient-info-form-edit.jsx,
// patient-search-form.jsx, ...) y el campo de la pantalla de identificación
// del wizard —justo el que alimenta el OCR— se había quedado sin máscara.
//
// Dos formatos, decididos por `documentType` (el MISMO enum de
// optionsDocumentTypes: '0' cédula, '1' pasaporte):
//
//   Cédula    -> máscara fija 999-9999999-9. `autoUnmask: true` hace que el
//                valor que llega a react-hook-form sean SOLO los dígitos: el
//                backend y getPatientByDocumentId reciben lo mismo que
//                recibían antes, los guiones son presentación.
//   Pasaporte -> sin máscara (el formato varía por país emisor) pero forzado
//                a MAYÚSCULAS en el onChange, no solo con text-transform:
//                el CSS no toca el valor guardado y el documento acabaría en
//                la base de datos en minúsculas.
//
// Cualquier otro valor de documentType (o ninguno) deja el campo en texto
// libre sin transformar: es el caso de la admisión rápida, donde el paciente
// puede no tener documento todavía.
// ----------------------------------------------------------------------

export const DOCUMENT_TYPE = Object.freeze({
  CEDULA: '0',
  PASAPORTE: '1',
});

// Sin `jitMasking` a propósito: con él la máscara solo se materializa a medida
// que se teclea, así que un documento cargado del servidor —que se guarda en
// dígitos— se veía como "00112345678" hasta que alguien tocaba el campo.
const OPCIONES_MASCARA_CEDULA = {
  mask: '999-9999999-9',
  greedy: false,
  placeholder: ' ',
  clearIncomplete: true,
  showMaskOnHover: false,
  showMaskOnFocus: false,
  autoUnmask: true,
};

function CampoDocumento({ field, error, esCedula, esPasaporte, helperText, inputProps, ...other }) {
  const inputRef = useRef(null);

  // La máscara se aplica al nodo DOM, así que hay que reaplicarla cuando el
  // campo se vuelve a montar (el wizard desmonta la pantalla entera al
  // navegar) y quitarla cuando el tipo deja de ser cédula. Sin el cleanup,
  // cambiar a pasaporte dejaba la máscara numérica viva y era imposible
  // teclear letras.
  useEffect(() => {
    const elemento = inputRef.current;
    if (!elemento) return undefined;

    if (!esCedula) {
      elemento.inputmask?.remove();
      return undefined;
    }

    new Inputmask(OPCIONES_MASCARA_CEDULA).mask(elemento);
    return () => elemento.inputmask?.remove();
  }, [esCedula]);

  // Inputmask mantiene su propio estado interno y NO se entera de un cambio de
  // value hecho por React. Al editar, el documento llega después del fetch, así
  // que sin esta sincronización el campo mostraba los dígitos crudos. Va después
  // del efecto de arriba a propósito: primero existe la máscara, luego se llena.
  useEffect(() => {
    const mascara = inputRef.current?.inputmask;
    if (!esCedula || !mascara) return;

    const valor = field.value == null ? '' : String(field.value);
    if (mascara.unmaskedvalue() !== valor) mascara.setValue(valor);
  }, [field.value, esCedula]);

  return (
    <TextField
      {...field}
      fullWidth
      inputRef={inputRef}
      value={field.value ?? ''}
      onChange={(event) => {
        const valor = event?.target?.value ?? '';
        field.onChange(esPasaporte ? valor.toUpperCase() : valor);
      }}
      placeholder={esCedula ? '000-0000000-0' : undefined}
      error={!!error}
      helperText={error ? error?.message : helperText}
      inputProps={{
        ...(esPasaporte ? { style: { textTransform: 'uppercase' }, maxLength: 20 } : null),
        ...inputProps,
      }}
      {...other}
    />
  );
}

CampoDocumento.propTypes = {
  field: PropTypes.object.isRequired,
  error: PropTypes.object,
  esCedula: PropTypes.bool,
  esPasaporte: PropTypes.bool,
  helperText: PropTypes.node,
  inputProps: PropTypes.object,
};

export default function RHFDocumentIdField({ name, documentType, helperText, inputProps, ...other }) {
  const { control } = useFormContext();

  const tipo = documentType === null || documentType === undefined ? '' : String(documentType);
  const esCedula = tipo === DOCUMENT_TYPE.CEDULA;
  const esPasaporte = tipo === DOCUMENT_TYPE.PASAPORTE;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <CampoDocumento
          field={field}
          error={error}
          esCedula={esCedula}
          esPasaporte={esPasaporte}
          helperText={helperText}
          inputProps={inputProps}
          {...other}
        />
      )}
    />
  );
}

RHFDocumentIdField.propTypes = {
  name: PropTypes.string.isRequired,
  /** '0' cédula dominicana | '1' pasaporte. Acepta number por comodidad. */
  documentType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  helperText: PropTypes.node,
  inputProps: PropTypes.object,
};
